import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from 'moment';
import _ from 'lodash';
import { endpoints } from "Core/defaultValues";
import { clientQuery } from "Core/data/GraphQLApi";
import { resetALL } from "Redux/actions";
import { ExceptionManager } from "Core/logManager";

const INIT_STATE = {
    loading: false,
    dashboard: {},
    gadgetsData: {
    }
};

const route = "modules/driverPerformance/components/dashboard/slice";

export const getDashboard = createAsyncThunk("dashboard/getDashboard",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { location } = payload
            let result = await clientQuery(
                `
                query GetDashboard($location: String!) {
                    data: getDashboard(location: $location) {
                        id
                        name
                        location
                        isStatic
                        gadgets {
                            id
                            dashboadId
                            gadgetKey
                            name
                            gadgetType
                            layout
                            refreshSec
                            settings
                        }
                    }
                }
                `,
                { location },
                endpoints.GRAPHQL_GENERAL_V2
            );

            let returnData = {};
            if (result.data) {

                returnData = _.cloneDeep(result.data);
                returnData.gadgets = returnData.gadgets.map(gadget => {
                    return {
                        ...gadget,
                        layout: gadget.layout && JSON.parse(JSON.parse(gadget.layout)) || {},
                        settings: gadget.settings && JSON.parse(JSON.parse(gadget.settings)) || {},
                    }
                })
            }

            return returnData;
        } catch (exc) {
            ExceptionManager(exc, route, "getDashboard");
            return rejectWithValue(exc);
        }
    }
);

export const getGadgetData = createAsyncThunk("dashboard/getGadgetData",
    async (payload, { rejectWithValue, getState }) => {
        const { gadgetkey, page = 0, pageSize = 10, filters = {}, sort, paginationType = 'normal', nextToken = null, moreData = false } = payload;
        let key = `${gadgetkey}`;
        let limit = pageSize;
        let offset = paginationType === 'infinite' ? null : (page - 1) * pageSize;

        const {
            tags = [],
            drivers = [],
            rangeDate,
            events = [],
            timeFrame,
            videoEvents = [],
            includeUnassignedDriver,
            tagHierarchyRootId,
            tagLevel
        } = filters;
        try {

            let conditions = [];
            let currentSort = sort?.key ? { field: sort.key, order: sort?.order === "asc" ? "ASC" : "DESC" } : null;

            if (drivers.length) {
                conditions.push({
                    field: "driverIds",
                    terms: drivers.map((item => item.driverId))
                })
            }

            if (tags.length) {
                conditions.push({
                    field: "tagIds",
                    terms: tags.map((item => item.id))
                })
            }

            if (events.length) {
                conditions.push({
                    field: "eventNames",
                    terms: events.map((item => item.id))
                })
            }

            if(videoEvents.length){
                conditions.push({
                    field: "videoEvents",
                    terms: videoEvents.map((item => item.id.replace(/\s+/g, '')))
                })
            }

           if (includeUnassignedDriver !== undefined && includeUnassignedDriver !== null) {
                conditions.push({
                    field: "includeUnassignedDriver",
                    terms: [includeUnassignedDriver]
                });
            }

            // Tag hierarchy filter conditions
            if (tagHierarchyRootId) {
                conditions.push({
                    field: "tagHierarchyRootId",
                    terms: [String(tagHierarchyRootId)]
                });
            }

            if (tagLevel !== null && tagLevel !== undefined) {
                conditions.push({
                    field: "tagLevel",
                    terms: [String(tagLevel)]
                });
            }

            let startUTC = rangeDate?.start || null;
            let endUTC = rangeDate?.end || null;

            if (rangeDate?.start) {
                if (timeFrame === 'custom') {
                    const startDate = moment(rangeDate.start).format('YYYY-MM-DD');
                    startUTC = moment.utc(startDate, 'YYYY-MM-DD').startOf('day').toDate();
                } else {
                    startUTC = moment(rangeDate.start).utc().toDate();
                }
            }

            if (rangeDate?.end) {
                if (timeFrame === 'custom') {
                    const endDate = moment(rangeDate.end).format('YYYY-MM-DD');
                    endUTC = moment.utc(endDate, 'YYYY-MM-DD').endOf('day').toDate();
                } else {
                    endUTC = moment(rangeDate.end).utc().toDate();
                }
            }

            let queryPayload = {
                conditions: conditions,
                gadgetKey: gadgetkey,
                sort: currentSort,
                limit,
                offset
            }

            // Only include timeFrame when date range is provided
            if (startUTC && endUTC) {
                queryPayload.timeFrame = {
                    GTE: startUTC,
                    LTE: endUTC
                };
            }

            // For infinite scroll, use nextToken instead of offset
            if (paginationType === 'infinite' && nextToken) {
                queryPayload.nextToken = nextToken;
                delete queryPayload.offset; // Don't use offset when using nextToken
            }

            const response = await clientQuery(`
                    query (
                        $gadgetKey: enumDashboardGadgetKey!
                        $timeFrame: RangeInput
                        $conditions: [GadgetDataSearchInput]
                        $sort: GadgetDataSortInput
                        $limit: Int
                        $offset: Int
                        $nextToken: String
                    ) {
                        data: searchDashboardGadgetData(
                            gadgetKey: $gadgetKey
                            timeFrame: $timeFrame
                            limit: $limit
                            offset: $offset
                            conditions: $conditions
                            sort: $sort
                            limit: $limit
                            offset: $offset
                            nextToken: $nextToken
                        ) {
                            total
                            items
                            nextToken
                        }
                    }
                `,
                queryPayload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            let data = {}
            if (response.data.items) {
                let items = JSON.parse(response.data.items);
                data = {
                    items: items,
                    total: response.data.total,
                    nextToken: response.data.nextToken || null // Include nextToken in the response
                }
            } else {
                data = { items: [], total: 0, nextToken: null }
            }

            return { key: key, data };
        } catch (exc) {
            ExceptionManager(exc, route, "getGadgetData");
            return rejectWithValue({ key: key, error: exc });
        }
    }
);

//Slice
const dashboardRedux = createSlice({
    name: "dashboard",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        builder.addCase(getDashboard.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(getDashboard.fulfilled, (state, action) => {
            state.loading = false;
            // if (action.payload && action.payload.distributionData) {
            //     state.distributionData = action.payload.distributionData;
            // }
            state.dashboard = action.payload || {}
        });
        builder.addCase(getDashboard.rejected, (state, action) => {
            state.loading = false;
        });

        ///getGadgetData
        builder.addCase(getGadgetData.pending, (state, action) => {
            const { paginationType = 'normal', moreData = false } = action.meta.arg;
            let key = `${action.meta.arg.gadgetkey}`;
            let valueKey = state.gadgetsData ? state.gadgetsData[key] : {};

            // Always maintain loading state
            state.gadgetsData[key] = { ...valueKey, loading: true };
        });
        builder.addCase(getGadgetData.fulfilled, (state, action) => {
            const { paginationType = 'normal', moreData = false } = action.meta.arg;
            let key = action.payload.key;
            const currentData = state.gadgetsData[key] || {};
            const newData = action.payload.data;

            // If moreData is true, append new items to the current list
            if (paginationType === 'infinite' && moreData === true) {
                const existingItems = currentData.data?.items || [];
                const newItems = newData.items || [];
                state.gadgetsData[key] = {
                    ...currentData,
                    loading: false,
                    data: {
                        ...newData,
                        items: [...existingItems, ...newItems],
                        nextToken: newData.nextToken // Save the new nextToken
                    }
                };
            } else {
                // If moreData is false, create new list with fresh data (replace completely)
                state.gadgetsData[key] = {
                    ...currentData,
                    loading: false,
                    data: newData
                };
            }
        });
        builder.addCase(getGadgetData.rejected, (state, action) => {
            let key = action.payload.key;
            state.gadgetsData[key] = { ...state.gadgetsData[key], loading: false, error: action.payload.error };
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = dashboardRedux;

// Extract and export each action creator by name
export const { reset } = actions;

// Export the reducer, either as a default or named export
export default reducer;
